import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Heading, Icon, Modal, Skeleton, Text, Toggle } from "@telia/teddy";
import cn from "classnames";
import _compact from "lodash/compact";
import _find from "lodash/find";
import _isEmpty from "lodash/isEmpty";
import { useSelector, useDispatch } from "react-redux";
import { GET_CUSTOMER_TYPE } from "../../../../../constants";
import { OfferingCode, RootState, SubscriptionWithGetIT } from "../../../../../models";
import { updateSubscriptions } from "../../../../utils/subscriptions";
import { isTypeLoading } from "../../../loading/state/selectors";
import { getModals, isFamilyDiscountModal } from "../../../modals/state/selectors";
import { RenderCfContent } from "../../../modals/utils/util";
import "./ExtraSubscriptionModal.less";
import { getSubscriptions } from "../../../subscription-listing/state/actions";
import { SUBSCRIPTION_LISTING_GET_SUBSCRIPTIONS } from "../../../subscription-listing/state/actionTypes";
import {
    getHiddenSubscriptionsOfferingCodes,
    getHiddenSubscriptionsYoungOfferingCodes,
    getSubscriptions as getSubscriptionsSelector,
    getSubscriptionsYoung as getYoungSubscriptionsSelector,
} from "../../../subscription-listing/state/selectors";
import SubscriptionCardList from "../../components/organisms/SubscriptionCardList/SubscriptionCardList";
import {
    getAllOfferingCodes,
    getCustomerTypeGet,
    getNumberOfSubscriptionsInCart,
} from "../../state/selectors/cartSelectors";
import { getSubscriptions as getCartSubscriptions } from "../../state/selectors/subscriptionsSelectors";
import { getContentfulFeatureToggles } from "../../../app/state/selectors";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (offeringCode: OfferingCode, quantity: number) => void;
    setIsOpen: (isOpen: boolean) => void;
}

const ExtraSubscriptionModal: React.FC<Props> = ({ onClose, onSubmit, isOpen, setIsOpen }) => {
    const dispatch = useDispatch();
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState("");
    const [isUnder30, setIsUnder30] = useState(false);
    const [showHiddenSubscriptions, setShowHiddenSubscriptions] = useState(false);
    const customerTypeGet = useSelector((state: RootState) => getCustomerTypeGet(state));
    const isFetchingSubscriptions = useSelector((state: RootState) =>
        isTypeLoading(state, SUBSCRIPTION_LISTING_GET_SUBSCRIPTIONS),
    );
    const familyInfoModal = useSelector((state: RootState) => _find(getModals(state), isFamilyDiscountModal));
    const offeringCodesInCart = useSelector((state: RootState) => getAllOfferingCodes(state));
    const subscriptionsInCart = useSelector((state: RootState) => getCartSubscriptions(state));
    const offerings = useSelector((state: RootState) => getSubscriptionsSelector(state));
    const offeringsYoung = useSelector((state: RootState) => getYoungSubscriptionsSelector(state));
    const hiddenSubscriptionsOfferingCodes = useSelector((state: RootState) =>
        getHiddenSubscriptionsOfferingCodes(state),
    );
    const hiddenSubscriptionsYoungOfferingCodes = useSelector((state: RootState) =>
        getHiddenSubscriptionsYoungOfferingCodes(state),
    );
    const numberOfSelectedSubscriptionInCart = useSelector((state: RootState) =>
        getNumberOfSubscriptionsInCart(state, selectedSubscription as OfferingCode),
    );
    const contentfulFeatureToggles = useSelector((state: RootState) => getContentfulFeatureToggles(state));
    const isSummerCampaign2026Enabled = contentfulFeatureToggles["enableSummerCampaign2026"];
    const isSummerCampaignJunior2026Enabled = contentfulFeatureToggles["enableSummerCampaignJunior2026"];

    const unlimitedSubscriptionsOfferingCodesInCart = useMemo(
        () =>
            _compact(
                Object.values(subscriptionsInCart)
                    .filter((sub) => sub.isDataUnlimited === true)
                    .map((sub) => sub.code?.split(".")[0]),
            ),
        [subscriptionsInCart],
    );

    const hiddenSubscriptions = useMemo(() => {
        if (isUnder30) {
            return hiddenSubscriptionsYoungOfferingCodes;
        }

        return hiddenSubscriptionsOfferingCodes.filter((offeringCode) => {
            if (unlimitedSubscriptionsOfferingCodesInCart.length > 0) {
                return !unlimitedSubscriptionsOfferingCodesInCart.includes(offeringCode);
            }
            return true;
        });
    }, [
        isUnder30,
        hiddenSubscriptionsOfferingCodes,
        hiddenSubscriptionsYoungOfferingCodes,
        unlimitedSubscriptionsOfferingCodesInCart,
    ]);

    const subscriptionsRaw = useMemo(() => {
        if (isUnder30) {
            return offeringsYoung;
        }

        return offerings.filter(
            (offering) =>
                unlimitedSubscriptionsOfferingCodesInCart.length === 0 ||
                !offering.dataUnlimited ||
                offeringCodesInCart.includes(offering.offeringCode),
        );
    }, [isUnder30, offerings, offeringsYoung, unlimitedSubscriptionsOfferingCodesInCart, offeringCodesInCart]);

    const isTeliaSamlet = customerTypeGet === GET_CUSTOMER_TYPE.EXISTING;

    const subscriptions = useMemo(
        () =>
            updateSubscriptions({
                subscriptions: subscriptionsRaw as SubscriptionWithGetIT[],
                shouldShowExistingGetOffer: isTeliaSamlet,
                shouldShowAdjustedSpeeds: true,
                shouldShowOnlyUnder30: isUnder30,
                isUnder30,
                showHiddenSubscriptions,
                hiddenSubscriptions: hiddenSubscriptions,
            }),
        [isUnder30, subscriptionsRaw, showHiddenSubscriptions, hiddenSubscriptions],
    );

    useEffect(() => {
        if (_isEmpty(offerings)) {
            dispatch(getSubscriptions());
        }
    }, []);

    return (
        <Modal open={isOpen}>
            <Modal.Overlay className="extra-subscription-modal__container">
                <Modal.Content className="extra-subscription-modal__content">
                    <Modal.Group className="extra-subscription-modal__header" variant="title">
                        <Modal.Title as="h3">
                            <span>Legg til flere - få rabatt</span>
                        </Modal.Title>
                        <Modal.Close aria-label="lukk" slot="floating" onClick={() => setIsOpen(false)} />
                    </Modal.Group>
                    {familyInfoModal && familyInfoModal.heading && (
                        <div
                            className={cn("extra-subscription-modal__family-discount-info", {
                                "extra-subscription-modal__family-discount-info--open": isInfoOpen,
                            })}
                        >
                            <Button
                                className="extra-subscription-modal__family-discount-info--button"
                                variant="text"
                                onClick={() => setIsInfoOpen(!isInfoOpen)}
                            >
                                {familyInfoModal.heading}
                                <Icon name="chevron-down" aria-hidden="true" focusable="false" />
                            </Button>
                            {isInfoOpen && (
                                <div className="extra-subscription-modal__family-discount-info--cf-content">
                                    <RenderCfContent modal={familyInfoModal} />
                                </div>
                            )}
                        </div>
                    )}
                    <Heading as="h3" variant="title-200" mt="200" mb="100">
                        Velg abonnement
                    </Heading>
                    <Text>Du kan endre senere på Mine Sider</Text>
                    <Box mt="100">
                        <Toggle>
                            <Toggle.Input checked={isUnder30} onCheckedChange={() => setIsUnder30(!isUnder30)} />
                            <Toggle.Label>Under 30 år</Toggle.Label>
                        </Toggle>
                    </Box>
                    {isFetchingSubscriptions &&
                        Array.from({ length: 2 }).map((_unused, index) => (
                            <Box width="100%" key={index}>
                                <Skeleton
                                    radius="sm"
                                    loading
                                    style={{ height: "160px", width: "100%", marginBottom: "1rem" }}
                                />
                            </Box>
                        ))}
                    {subscriptions.length > 0 && (
                        <>
                            <SubscriptionCardList
                                idPrefix="single"
                                isPDP
                                subscriptions={subscriptions as SubscriptionWithGetIT[]}
                                selectedSubscriptionCode={selectedSubscription.split(".")[0]}
                                numberOfSubscriptions={1}
                                showExistingGetOffer={isTeliaSamlet}
                                showTextTypes={["info", "checkout"]}
                                showOnlyGivenTextTypes
                                priceInfo={["/md."]}
                                isSummerCampaign2026Enabled={isSummerCampaign2026Enabled}
                                isSummerCampaignJunior2026Enabled={isSummerCampaignJunior2026Enabled}
                                onOpenSubscription={(offeringCode, icim) => {
                                    const pricePlan = `${offeringCode}.${icim || "REGULAR"}`;
                                    if (offeringCode === selectedSubscription) {
                                        setSelectedSubscription("");
                                        return;
                                    }
                                    setSelectedSubscription(pricePlan);
                                }}
                            />
                            {!_isEmpty(hiddenSubscriptions) && (
                                <>
                                    <Button
                                        variant="text"
                                        onClick={(): void => {
                                            setShowHiddenSubscriptions(!showHiddenSubscriptions);
                                        }}
                                    >
                                        <span>
                                            {!showHiddenSubscriptions ? "Se flere abonnement" : "Se færre abonnement"}
                                        </span>
                                        <Icon
                                            name={!showHiddenSubscriptions ? "chevron-down" : "chevron-up"}
                                            aria-hidden="true"
                                            focusable="false"
                                        />
                                    </Button>
                                </>
                            )}
                        </>
                    )}
                    <Modal.Group className="extra-subscription-modal__footer" variant="action">
                        <Modal.Close slot="content">
                            <Button variant="secondary" onClick={onClose}>
                                <span>Tilbake</span>
                            </Button>
                        </Modal.Close>
                        <Button
                            variant="primary"
                            loading={false}
                            onClick={() =>
                                onSubmit(selectedSubscription as OfferingCode, numberOfSelectedSubscriptionInCart + 1)
                            }
                            disabled={false}
                        >
                            <span>Legg til abonnement</span>
                        </Button>
                    </Modal.Group>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export default ExtraSubscriptionModal;
